import { format, subDays } from 'date-fns'
import { ArrowLeft, BarChart3, ChevronRight, Download } from 'lucide-react'
import { useState } from 'react'
import type { AdvertiserCampaignBreakdown } from '@/api/hooks/use-campaign-analytics'
import { useAdvertiserAnalytics, useAnalyticsExport } from '@/api/hooks/use-campaign-analytics'
import { DataTable } from '@/components/ui/data-table'
import type { DateRange } from '@/components/ui/date-range-picker'
import { DateRangePicker } from '@/components/ui/date-range-picker'
import { MetricCard } from '@/components/ui/metric-card'
import { buildSvgChart } from '@/lib/report-template'

export function AnalyticsByAdvertiser(): React.JSX.Element {
  const [dateRange, setDateRange] = useState<DateRange>({
    from: subDays(new Date(), 90),
    to: new Date(),
  })
  const [selectedDomain, setSelectedDomain] = useState<string | null>(null)
  const exportMutation = useAnalyticsExport()

  const params = {
    from: format(dateRange.from, 'yyyy-MM-dd'),
    to: format(dateRange.to, 'yyyy-MM-dd'),
    advertiser: selectedDomain ?? undefined,
  }

  const { data, isLoading } = useAdvertiserAnalytics(params)
  const advertisers = data?.advertisers ?? []
  const byCampaign = data?.by_campaign ?? []
  const selected = selectedDomain ? advertisers.find((a) => a.domain === selectedDomain) : null

  const totalClicks = advertisers.reduce((s, a) => s + (a.clicks ?? 0), 0)
  const totalUnique = advertisers.reduce((s, a) => s + (a.unique_clicks ?? 0), 0)

  function handleCsvDownload(): void {
    exportMutation.mutate({
      type: 'advertiser',
      advertiser_domain: selectedDomain ?? undefined,
      from: format(dateRange.from, 'yyyy-MM-dd'),
      to: format(dateRange.to, 'yyyy-MM-dd'),
      format: 'csv',
    })
  }

  return (
    <div className="space-y-6">
      {/* Controls — date picker always visible */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-3">
          {selectedDomain && (
            <button
              type="button"
              onClick={() => setSelectedDomain(null)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-surface-3 px-3 py-2 text-sm font-medium text-text-primary hover:bg-surface-1"
            >
              <ArrowLeft className="h-4 w-4" /> All advertisers
            </button>
          )}
          <DateRangePicker value={dateRange} onChange={setDateRange} />
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={handleCsvDownload}
            disabled={exportMutation.isPending}
            className="inline-flex items-center gap-1.5 rounded-lg border border-surface-3 px-3 py-2 text-sm font-medium text-text-primary hover:bg-surface-1 disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            {exportMutation.isPending
              ? 'Exporting...'
              : `CSV${selectedDomain ? ` (${selectedDomain})` : ''}`}
          </button>
          <button
            type="button"
            onClick={() =>
              exportMutation.mutate({
                type: 'advertiser',
                advertiser_domain: selectedDomain ?? undefined,
                from: format(dateRange.from, 'yyyy-MM-dd'),
                to: format(dateRange.to, 'yyyy-MM-dd'),
                format: 'pdf',
              })
            }
            disabled={exportMutation.isPending}
            className="inline-flex items-center gap-1.5 rounded-lg border border-surface-3 px-3 py-2 text-sm font-medium text-text-primary hover:bg-surface-1 disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            PDF
          </button>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {selected ? (
          <>
            <MetricCard label="Advertiser" value={selected.domain} />
            <MetricCard label="Clicks" value={(selected.clicks ?? 0).toLocaleString()} />
            <MetricCard
              label="Unique Clicks"
              value={(selected.unique_clicks ?? 0).toLocaleString()}
            />
            <MetricCard label="Campaigns" value={String(selected.campaigns ?? 0)} />
          </>
        ) : (
          <>
            <MetricCard label="Advertisers" value={String(advertisers.length)} />
            <MetricCard label="Total Clicks" value={totalClicks.toLocaleString()} />
            <MetricCard label="Unique Clicks" value={totalUnique.toLocaleString()} />
            <MetricCard
              label="Campaigns"
              value={String(advertisers.reduce((s, a) => s + (a.campaigns ?? 0), 0))}
            />
          </>
        )}
      </div>

      {/* Advertiser detail: chart + campaign breakdown */}
      {selectedDomain && byCampaign.length > 0 && (
        <>
          <div className="rounded-xl border border-surface-3 bg-surface-0 p-5">
            <h3 className="mb-3 text-sm font-medium text-text-secondary">Clicks by Campaign</h3>
            <div
              // biome-ignore lint/security/noDangerouslySetInnerHtml: intentional for rendered HTML content
              dangerouslySetInnerHTML={{
                __html: buildSvgChart(
                  byCampaign.map((c) => ({
                    label: c.sent_at ? format(new Date(c.sent_at), 'MMM d') : '?',
                    opens: c.sent ?? 0,
                    clicks: c.clicks ?? 0,
                  })),
                ),
              }}
            />
          </div>

          <div>
            <h3 className="mb-3 text-sm font-medium text-text-secondary">Campaign Breakdown</h3>
            <DataTable
              columns={[
                {
                  key: 'campaign_name',
                  label: 'Campaign',
                  render: (r: AdvertiserCampaignBreakdown) => (
                    <span className="font-medium">{r.campaign_name ?? 'Unknown'}</span>
                  ),
                },
                {
                  key: 'sent_at',
                  label: 'Date',
                  render: (r: AdvertiserCampaignBreakdown) => {
                    try {
                      return format(new Date(r.sent_at), 'MMM d, yyyy')
                    } catch {
                      return '—'
                    }
                  },
                },
                {
                  key: 'sent',
                  label: 'Sent',
                  render: (r: AdvertiserCampaignBreakdown) => (r.sent ?? 0).toLocaleString(),
                },
                {
                  key: 'clicks',
                  label: 'Clicks',
                  render: (r: AdvertiserCampaignBreakdown) => (r.clicks ?? 0).toLocaleString(),
                },
                {
                  key: 'ctr',
                  label: 'CTR',
                  render: (r: AdvertiserCampaignBreakdown) => `${(r.ctr ?? 0).toFixed(2)}%`,
                },
              ]}
              data={byCampaign}
            />
          </div>
        </>
      )}

      {/* Advertiser list (when not drilled in) */}
      {!selectedDomain && (
        <div>
          <h3 className="mb-3 text-sm font-medium text-text-secondary">All Advertisers</h3>
          {isLoading ? (
            <p className="py-8 text-center text-sm text-text-tertiary">Loading...</p>
          ) : advertisers.length === 0 ? (
            <div className="flex flex-col items-center py-12 text-center">
              <BarChart3 className="mb-3 h-10 w-10 text-text-tertiary" />
              <p className="text-sm text-text-tertiary">No ad click data in this period.</p>
              <p className="mt-1 text-xs text-text-secondary">
                Ad slots in your newsletters will appear here once recipients click them.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {advertisers.map((a) => (
                <button
                  type="button"
                  key={a.domain}
                  onClick={() => setSelectedDomain(a.domain)}
                  className="flex w-full items-center justify-between rounded-lg border border-surface-3 bg-surface-0 p-4 text-left transition-colors hover:bg-surface-1"
                >
                  <div>
                    <p className="font-medium text-text-primary">{a.domain}</p>
                    <p className="mt-0.5 text-xs text-text-secondary">
                      First seen {(() => {
                        try {
                          return format(new Date(a.first_seen), 'MMM d, yyyy')
                        } catch {
                          return '—'
                        }
                      })()}
                      {' · '}
                      {a.campaigns ?? 0} campaign{(a.campaigns ?? 0) !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm font-semibold text-text-primary">
                        {(a.clicks ?? 0).toLocaleString()} clicks
                      </p>
                      <p className="text-xs text-text-secondary">
                        {(a.unique_clicks ?? 0).toLocaleString()} unique
                      </p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-text-tertiary" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {selectedDomain && byCampaign.length === 0 && !isLoading && (
        <p className="py-8 text-center text-sm text-text-tertiary">
          No campaign data for {selectedDomain} in this period.
        </p>
      )}
    </div>
  )
}
